import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, Chip, Modal } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { money } from '../lib/scope';

/**
 * WHAT A DOCTOR IS OWED FOR THE MONTH, and the lines that move it.
 *
 * The number is derived every time it is opened — collected × the share the
 * manager set in Money — because a stored payout and a live ledger drift apart
 * the first time a claim lands late. The only typed part is the adjustments:
 * an advance, a lab fee the clinic swallowed, a bonus.
 *
 * COLLECTED, NOT BILLED, is the base, the same rule the ledger prints above it.
 */

interface Adjustment {
  id: string;
  kind: 'deduction' | 'addition';
  label: string;
  amountMinor: number;
}

interface Payout {
  doctorId: string;
  doctorName: string;
  period: string;
  collectedMinor: number;
  billedMinor: number;
  sharePercent: number | null;
  grossMinor: number | null;
  adjustments: Adjustment[];
  deductionsMinor: number;
  additionsMinor: number;
  netMinor: number | null;
}

const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;
const egp = (minor: number) => money(minor / 100);

function usePayout(period: string, doctorId: string, open: boolean) {
  return useQuery({
    queryKey: ['cm-payout', period, doctorId],
    enabled: open && Boolean(doctorId),
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<Payout | null> => {
      const res = await apiClient.get('/doctor-payouts', { params: { period, doctorId } });
      return unwrap<Payout[]>(res.data)?.[0] ?? null;
    },
  });
}

export function PayoutDetail({
  open,
  onClose,
  doctorId,
  doctorName,
  period,
  periodLabel,
}: {
  open: boolean;
  onClose: () => void;
  doctorId: string;
  doctorName: string;
  /** `YYYY-MM`. */
  period: string;
  periodLabel: string;
}) {
  const qc = useQueryClient();
  const go = useNavigate();
  const { data: payout, isLoading } = usePayout(period, doctorId, open);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<'deduction' | 'addition'>('deduction');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['cm-payout', period, doctorId] });

  const add = useMutation({
    mutationFn: async () => {
      await apiClient.post('/doctor-payouts/adjustments', {
        doctorId,
        period,
        kind,
        label: label.trim(),
        amountMinor: Math.round(Number(amount) * 100),
      });
    },
    onSuccess: () => {
      setAdding(false); setLabel(''); setAmount('');
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/doctor-payouts/adjustments/${id}`); },
    onSuccess: () => { void invalidate(); },
  });

  const amountOk = Number(amount) > 0 && label.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${doctorName} — ${periodLabel}`}
      sub="collected × share, then what the month moved"
      width={560}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            Add a line
          </button>
        </>
      }
    >
      <div style={{ paddingTop: 12 }}>
        {isLoading || !payout ? (
          <Card><p className="t-sec mut">Reading the month…</p></Card>
        ) : (
          <>
            <Card>
              <p className="t-ovl mut">THE BASE</p>
              <p className="t-strong mt4">{egp(payout.collectedMinor)} collected</p>
              <p className="t-cap mut">
                of {egp(payout.billedMinor)} billed · cash and claims paid, never billed
              </p>
            </Card>

            {payout.sharePercent === null ? (
              /* A share is a decision, and Money is where it is made. One line
                 and the door to it — not an explanation. */
              <Card style={{ borderColor: 'var(--warning-600)' }}>
                <p className="t-ovl mut">SHARE</p>
                <p className="t-strong mt4" style={{ color: 'var(--warning-600)' }}>Not set</p>
                <button type="button" className="btn btn-ghost" style={{ marginTop: 8 }}
                  onClick={() => { onClose(); go('/clinic-manager/settings/money'); }}>
                  Set it in Money ›
                </button>
              </Card>
            ) : (
              <Card>
                <p className="t-ovl mut">SHARE</p>
                <p className="t-strong mt4">
                  {payout.sharePercent}% · {egp(payout.grossMinor ?? 0)}
                </p>
              </Card>
            )}

            <Card>
              <p className="t-ovl mut">LINES</p>
              {payout.adjustments.length === 0 ? (
                <p className="t-sec mut" style={{ marginTop: 4 }}>Nothing moved this month.</p>
              ) : (
                payout.adjustments.map((a) => (
                  <div key={a.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <Chip kind={a.kind === 'deduction' ? 'danger' : 'soft'} small>
                      {a.kind === 'deduction' ? '−' : '+'}{egp(a.amountMinor)}
                    </Chip>
                    <span className="t-sec grow">{a.label}</span>
                    <button type="button" className="btn btn-ghost"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(a.id)}>Remove</button>
                  </div>
                ))
              )}
            </Card>

            <Card kind="accent">
              <p className="t-ovl mut">NET</p>
              <p className="t-title mt4">
                {payout.netMinor === null ? '—' : egp(payout.netMinor)}
              </p>
              {payout.netMinor === null ? null : (
                <p className="t-cap mut">
                  {egp(payout.grossMinor ?? 0)} − {egp(payout.deductionsMinor)} + {egp(payout.additionsMinor)}
                </p>
              )}
            </Card>
          </>
        )}
      </div>

      {/* The one typed part of a payout, asked for in one place. */}
      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="A line on this payout"
        sub="an advance, a lab fee, a bonus"
        width={420}
        foot={
          <>
            <button type="button" className="btn btn-outline" onClick={() => setAdding(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={!amountOk || add.isPending}
              onClick={() => add.mutate()}>Add it</button>
          </>
        }
      >
        <div style={{ paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['deduction', 'addition'] as const).map((k) => (
              <Chip key={k} kind={kind === k ? 'brand' : 'outline'} small onClick={() => setKind(k)}>
                {k === 'deduction' ? 'Takes away' : 'Adds'}
              </Chip>
            ))}
          </div>
          <input
            className="dr-input" placeholder="What is it — “Lab fees”, “Advance 12 Sep”"
            value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120}
            style={{ width: '100%', minHeight: 44, padding: '10px 12px', fontFamily: 'inherit',
              fontSize: 'inherit', border: '1px solid var(--stroke-200)',
              borderRadius: 'var(--r-sm)', background: 'transparent', color: 'var(--ink-900)' }}
          />
          <input
            inputMode="decimal" placeholder="Amount in EGP"
            value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            style={{ width: '100%', minHeight: 44, padding: '10px 12px', fontFamily: 'inherit',
              fontSize: 'inherit', border: '1px solid var(--stroke-200)',
              borderRadius: 'var(--r-sm)', background: 'transparent', color: 'var(--ink-900)' }}
          />
        </div>
      </Modal>
    </Modal>
  );
}
